import type { CurrentClosePeriodDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { daysUntil, reportTypeName } from '../utils';

/**
 * The Close hub's main message while a report is overdue and was never filed. States expect
 * reports in order, so the late one comes first; the button opens the same File workflow as
 * the strip's "Ready to file". Written for treasurers who are not technical: warm, plain, no
 * codes. The report name is the same one the masthead heading shows.
 */
export function CloseOverdueNotice({
  period,
  onFile,
}: {
  period: CurrentClosePeriodDto;
  onFile: () => void;
}) {
  const daysLate = Math.max(1, -daysUntil(period.dueDate));
  const name = reportTypeName(period.name);
  const reportName = /\breport\b/i.test(name) ? name : `${name} report`;

  return (
    <div
      data-slot="close-overdue"
      className="mx-auto flex w-full max-w-lg flex-col items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center"
    >
      <h2 className="text-xl font-semibold text-slate-900">
        Your {reportName} is {daysLate} {daysLate === 1 ? 'day' : 'days'} late.
      </h2>
      <p className="text-base leading-relaxed text-slate-700">
        File it now. Everything you&apos;ve recorded for it is below, ready to review.
      </p>
      <p className="text-sm leading-relaxed text-slate-600">
        The state may charge a late fee. If it does, you&apos;ll settle that with them directly.
      </p>
      <Button type="button" size="lg" onClick={onFile}>
        File this report
      </Button>
    </div>
  );
}
